import { CSSProperties } from 'react'
import Slider, { Range } from 'rc-slider'
import 'rc-slider/assets/index.css'
import Colors from 'modules/Colors'
import { clsx, styx } from 'modules/Utils'

interface Mark {
    label: string
    description: string
    placement?: 'first' | 'middle' | 'last'
    value: number
}

interface SliderSymphonyProps {
    /** Optional className for the container */
    className: string
    /** Optional style overrides */
    style?: CSSProperties
    /** Current value of the slider - can be single number or tuple for range */
    value: number | [number, number]
    /** Offset for the slider */
    sliderOffset: number
    /** Minimum value allowed */
    min: number
    /** Maximum value allowed */
    max: number
    /** Array of marks to display on the slider */
    marks: Mark[]
    /** Optional value mapping for non-linear sliders */
    valueMap?: Record<number, number>
    /** Callback when slider value changes */
    onChange: (value: number | [number, number]) => void
    /** Whether the slider is a range slider */
    isRange?: boolean
    /** Whether the slider is disabled */
    disabled?: boolean
}

const SliderSymphony = ({
    className = '',
    style = {},
    value,
    sliderOffset = 0,
    min,
    max,
    marks,
    valueMap = {},
    onChange,
    isRange = false,
    disabled = false
}: SliderSymphonyProps) => {
    /** Converts display value back to actual value if valueMap is provided */
    const reverseValueMap: Record<number, number> = Object.entries(valueMap).reduce((acc, [actual, display]) => ({
        ...acc,
        [display]: parseInt(actual)
    }), {})

    /** Static styles for the Symphony slider component */
const sliderStyles = {
    /** Track styles for the slider */
    track: {
        backgroundColor: disabled ? Colors.grey : Colors.purpleLight,
        height: 24,
        borderRadius: 100,
    },
    /** Rail styles (the background track) */
    rail: {
        backgroundColor: disabled ? "#ddd" : '#e1d9eb',
        height: 24,
        borderRadius: 100,
    },
    /** Handle styles for the slider */
    handle: {
        borderColor: disabled ? Colors.greyDark : Colors.purple,
        backgroundColor: disabled ? Colors.greyDark : Colors.purple,
        opacity: 1,
        width: 34,
        height: 34,
        marginTop: -6,
        boxShadow: 'none'
    },
    /** Styles for the mark text below the slider */
    marks: {
        fontSize: 12,
        marginTop: 8,
        color: Colors.grey,
    },
    /** Styles for the mark dots on the slider */
    dot: {
        borderColor: disabled ? Colors.greyDark : Colors.purple,
        backgroundColor: disabled ? Colors.greyDark : Colors.purple,
        width: 8,
        height: 8,
        bottom: -12,
    }
}

    /** Safely maps a value through the valueMap with type checking */
    const mapValue = (val: number): number => {
        return valueMap[val] !== undefined ? valueMap[val] : val
    }

    const renderMarks = (mark: Mark) => {
        const markStyle = { marginTop: 30 }

        let markPlacement: CSSProperties = { alignItems: 'center' }
        if (mark.placement === 'first') {
            markPlacement = { alignItems: 'flex-start', marginLeft: 80 }
        } else if (mark.placement === 'last') {
            markPlacement = { alignItems: 'flex-end', marginRight: 80 }
        }

        return {
            style: markStyle,
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120, ...markPlacement }}>
                    <div>{mark.label}</div>
                    <div>{mark.description}</div>
                </div>
            )
        }
    }

    const formattedMarks = marks.reduce((acc, mark) => ({
        ...acc,
        [valueMap[mark.value] || mark.value]: renderMarks(mark)
    }), {})

    return (
        <div
            className={clsx('sliderSymphonyElement', className)}
            style={styx(
                sty.sliderSymphonyElement,
                {
                    backgroundColor: disabled ? Colors.greyLighter : Colors.purpleLightest,
                },
                style
            )}
        >
            {isRange ?
                <Range
                    style={{ marginTop: -16 }}
                    min={min - sliderOffset}
                    max={max + sliderOffset}
                    marks={formattedMarks}
                    value={[
                        mapValue(Array.isArray(value) ? value[0] : min),
                        mapValue(Array.isArray(value) ? value[1] : max)
                    ]}
                    onChange={(newValue: number[]) => {
                        const actualValues = newValue.map(v => reverseValueMap[v] || v)
                        onChange(actualValues as [number, number])
                    }}
                    trackStyle={[sliderStyles.track]}
                    railStyle={sliderStyles.rail}
                    handleStyle={[sliderStyles.handle, sliderStyles.handle]}
                    dotStyle={sliderStyles.dot}
                    allowCross={false}
                    pushable={true}
                    disabled={disabled}
                />
                :
                <Slider
                    style={{ marginTop: -16 }}
                    min={min - sliderOffset}
                    max={max + sliderOffset}
                    marks={formattedMarks}
                    value={mapValue(value as number)}
                    onChange={(newValue) => {
                        const actualValue = reverseValueMap[newValue as keyof typeof reverseValueMap] || newValue
                        onChange(actualValue)
                    }}
                    trackStyle={sliderStyles.track}
                    railStyle={sliderStyles.rail}
                    handleStyle={sliderStyles.handle}
                    dotStyle={sliderStyles.dot}
                    disabled={disabled}
                />
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    sliderSymphonyElement: {
        width: '100%',
        marginTop: 16,
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 14,
        paddingRight: 14,
        borderRadius: 100,
        marginBottom: 24,
    },
}

export default SliderSymphony